import { SEO_MODULE_BASKET, SEO_MODULE_CAREER_JOB, SEO_MODULE_CAREER_NEWS, SEO_MODULE_CATALOG, SEO_MODULE_CATEGORY, SEO_MODULE_CMS, SEO_MODULE_COMPARE, SEO_MODULE_CSR, SEO_MODULE_FAQ, SEO_MODULE_FOOD_DELIVERY_MAP, SEO_MODULE_GIFT_CARD_REQUEST, SEO_MODULE_GLOBAL, SEO_MODULE_INSPIRATIONS, SEO_MODULE_LOYALTY_FAQ, SEO_MODULE_NEWS, SEO_MODULE_PETROL_FAQ, SEO_MODULE_PRESS, SEO_MODULE_PRIZE_GAME, SEO_MODULE_PRODUCT, SEO_MODULE_SDS, SEO_MODULE_UNSUBSCRIBE } from '~~/common/config/SeoConfig';
import type { SeoModule } from '~~/common/stores/seo';
import type { UrlResourceStoreType } from '~~/common/stores/urlResource';

const seoIdExtractors = {
  product: (resourceId: string) => resourceId,
  category: (resourceId: string) => resourceId?.matchAll(/[0-9]+/g).next().value?.[0],
  boutique: (resourceId: string) => resourceId?.match(/[0-9]*\|([0-9]+)/)?.[1],
  cms_content: (resourceId: string) => resourceId,
};
const seoModules: Record<string, SeoModule> = {
  product: SEO_MODULE_PRODUCT,
  category: SEO_MODULE_CATEGORY,
  boutique: SEO_MODULE_CATEGORY,
  cms_content: SEO_MODULE_CMS,
  sds: SEO_MODULE_SDS,
  catalog: SEO_MODULE_CATALOG,
  giftCardRequest: SEO_MODULE_GIFT_CARD_REQUEST,
  csrSeoResource: SEO_MODULE_CSR,
  faq: SEO_MODULE_FAQ,
  foodDeliveryMap: SEO_MODULE_FOOD_DELIVERY_MAP,
  inspirations: SEO_MODULE_INSPIRATIONS,
  news: SEO_MODULE_NEWS,
  press: SEO_MODULE_PRESS,
  prizeGame: SEO_MODULE_PRIZE_GAME,
  unsubscribe: SEO_MODULE_UNSUBSCRIBE,
  loyaltyFaq: SEO_MODULE_LOYALTY_FAQ,
  petrolFaq: SEO_MODULE_PETROL_FAQ,
  compare: SEO_MODULE_COMPARE,
  careerNews: SEO_MODULE_CAREER_NEWS,
  careerJob: SEO_MODULE_CAREER_JOB,
  basket: SEO_MODULE_BASKET,
};

export function getSeoModuleFromUrlResource (urlResource: { resourceType: string; resourceId: string } | undefined, urlResourceStore: UrlResourceStoreType, to: { path: string }, $i18n: { localeCodes: { value: string[] } }): { seoModule: SeoModule; id?: number | string } {
  let seoModule: SeoModule | undefined;
  let seoModuleId: number | string | undefined;
  if (urlResource) {
    const {
      resourceId,
      resourceType,
    } = urlResource;
    seoModule = seoModules[resourceType];
    seoModuleId = seoIdExtractors[resourceType as keyof typeof seoIdExtractors]?.(resourceId);
  }
  if (seoModule === undefined) {
    if (isUrlResourceStoreCurrent(urlResourceStore, to, $i18n)) {
      seoModule = seoModules[urlResourceStore.data.resourceType ?? ''] ?? SEO_MODULE_GLOBAL;
      seoModuleId = urlResourceStore.data.seoResourceId;
    } else {
      seoModule = SEO_MODULE_GLOBAL;
      seoModuleId = -1;
    }
  }
  return {
    seoModule,
    id: seoModuleId,
  };
}

function removeTrailingSlash (url: string) {
  return url.length > 1 ? url.replace(/\/+$/, '') : url;
}

// `resetPath()` strips the trailing slash before every url-resource lookup, so
// the backend only ever sees — and returns — the slash-less slug. Both sides are
// normalised here to match: otherwise `/foo/` makes the store look stale, and the
// page falls back to SEO_MODULE_GLOBAL, which also skips the CMS content fetch.
function isUrlResourceStoreCurrent (urlResourceStore: UrlResourceStoreType, to: { path: string }, $i18n: { localeCodes: { value: string[] } }) {
  const urlData = urlResourceStore?.data || {};
  const path = removeTrailingSlash(to.path);
  const langlessPath = path.replace(
    new RegExp(
      `^(${$i18n.localeCodes.value.map(lc => '/' + lc).join('|')})($|/)`,
    ),
    '/',
  );
  const slug = removeTrailingSlash(urlData.slug ?? '');
  return (urlData.resourceType
      && (
        slug === path
          || slug === path.replace(/^\//, '')
          || slug === langlessPath
          || slug === langlessPath.replace(/^\//, '')
      )
  );
}
